import { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { has, isArray } from "lodash";

import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { socketConnection } from "../../services/socket";
import moment from "moment";

// evita várias tentativas simultâneas de refresh
let isRefreshing = false;
let failedQueue = [];

const processQueue = (token = null) => {
  failedQueue.forEach(promise => promise.resolve(token));
  failedQueue = [];
};

const clearAuthAndGoLogin = (history, setIsAuth, setUser) => {
  localStorage.removeItem("token");
  api.defaults.headers.Authorization = undefined;
  setIsAuth(false);
  setUser({});

  if (window.location.pathname !== "/login") {
    history.push("/login");
  }
};

const useAuth = () => {
  const history = useHistory();
  const [isAuth, setIsAuth] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState({});
  const [socket, setSocket] = useState({});

  // ✅ REGISTRAR INTERCEPTORS UMA ÚNICA VEZ
  useEffect(() => {
    const reqId = api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem("token");

        if (token) {
          try {
            config.headers["Authorization"] = `Bearer ${JSON.parse(token)}`;
          } catch (_) {
            localStorage.removeItem("token");
            delete config.headers["Authorization"];
          }
        } else {
          delete config.headers["Authorization"];
        }

        return config;
      },
      (error) => Promise.reject(error)
    );

    const resId = api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error?.config || {};
        const status = error?.response?.status;
        const url = originalRequest?.url || "";

        const isAuthRoute =
          url.includes("/auth/login") ||
          url.includes("/auth/refresh_token") ||
          url.includes("/auth/logout");

        // ✅ Se for o próprio refresh retornando 401/403 -> desloga sem estourar overlay
        if ((status === 401 || status === 403) && url.includes("/auth/refresh_token")) {
          // libera fila (sem rejeitar) pra não estourar overlay
          processQueue(null);
          clearAuthAndGoLogin(history, setIsAuth, setUser);
          return Promise.resolve({ data: null });
        }

        // ✅ Se já tentou 1x e ainda deu 401/403 -> desloga sem estourar overlay
        if ((status === 401 || status === 403) && originalRequest._retry && !isAuthRoute) {
          processQueue(null);
          clearAuthAndGoLogin(history, setIsAuth, setUser);
          return Promise.resolve({ data: null });
        }

        // ✅ Token expirado/ruim pode vir 401 OU 403
        if ((status === 401 || status === 403) && !originalRequest._retry && !isAuthRoute) {
          originalRequest._retry = true;

          // Se já tem refresh em andamento, enfileira e espera
          if (isRefreshing) {
            return new Promise((resolve) => {
              failedQueue.push({ resolve });
            }).then((token) => {
              // se não tem token, engole
              if (!token) return Promise.resolve({ data: null });

              originalRequest.headers["Authorization"] = `Bearer ${token}`;
              return api(originalRequest);
            });
          }

          isRefreshing = true;

          try {
            const { data } = await api.post("/auth/refresh_token");

            // ✅ Refresh respondeu, mas não trouxe token -> tratar como deslogado (sem throw)
            if (!data?.token) {
              processQueue(null);
              clearAuthAndGoLogin(history, setIsAuth, setUser);
              return Promise.resolve({ data: null });
            }

            localStorage.setItem("token", JSON.stringify(data.token));
            api.defaults.headers.Authorization = `Bearer ${data.token}`;
            setIsAuth(true);
            if (data?.user) setUser(data.user);

            // libera fila com token novo
            processQueue(data.token);

            originalRequest.headers["Authorization"] = `Bearer ${data.token}`;
            return api(originalRequest);
          } catch (err) {
            // refresh falhou -> desloga, mas NÃO rejeita fila (pra não estourar overlay)
            processQueue(null);
            clearAuthAndGoLogin(history, setIsAuth, setUser);
            return Promise.resolve({ data: null });
          } finally {
            isRefreshing = false;
          }
        }

        return Promise.reject(error);
      }
    );

    return () => {
      api.interceptors.request.eject(reqId);
      api.interceptors.response.eject(resId);
    };
  }, [history]);

  useEffect(() => {
    const token = localStorage.getItem("token");

    (async () => {
      if (token) {
        try {
          const { data } = await api.post("/auth/refresh_token");
          if (data?.token) {
            localStorage.setItem("token", JSON.stringify(data.token));
            api.defaults.headers.Authorization = `Bearer ${data.token}`;
            setIsAuth(true);
          } else {
            localStorage.removeItem("token");
            api.defaults.headers.Authorization = undefined;
            setIsAuth(false);
            setUser({});
          }
          if (data?.user) setUser(data.user);
        } catch (err) {
          localStorage.removeItem("token");
          api.defaults.headers.Authorization = undefined;
          setIsAuth(false);
          setUser({});
        }
      }

      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    if (Object.keys(user).length && user.id > 0) {
      let io;

      if (!Object.keys(socket).length) {
        io = socketConnection({ user });
        setSocket(io);
      } else {
        io = socket;
      }

      io.on(`company-${user.companyId}-user`, (data) => {
        if (data.action === "update" && data.user.id === user.id) {
          setUser(data.user);
        }
      });

      return () => {
        io.off(`company-${user.companyId}-user`);
      };
    }
  }, [user]); // eslint-disable-line

  const handleLogin = async (userData) => {
    setLoading(true);

    try {
      const { data } = await api.post("/auth/login", userData);
      const {
        user: { company },
      } = data;

      if (has(company, "companieSettings") && isArray(company.companieSettings[0])) {
        const setting = company.companieSettings[0].find(
          (s) => s.key === "campaignsEnabled"
        );
        if (setting && setting.value === "true") {
          localStorage.setItem("cshow", null);
        }
      }

      if (has(company, "companieSettings") && isArray(company.companieSettings[0])) {
        const setting = company.companieSettings[0].find(
          (s) => s.key === "sendSignMessage"
        );

        const signEnable = setting?.value === "enable";
        if (setting && setting.value === "enabled") {
          localStorage.setItem("sendSignMessage", signEnable);
        }
      }

      localStorage.setItem("profileImage", data.user.profileImage);

      moment.locale("pt-br");
      let dueDate;
      if (data.user.company.id === 1) {
        dueDate = "2999-12-31T00:00:00.000Z";
      } else {
        dueDate = data.user.company.dueDate;
      }

      const vencimento = moment(dueDate).format("DD/MM/yyyy");
      const diff = moment(dueDate).diff(moment());
      const before = moment().isBefore(dueDate);
      const dias = moment.duration(diff).asDays();

      if (before === true) {
        localStorage.setItem("token", JSON.stringify(data.token));
        localStorage.setItem("companyDueDate", vencimento);

        api.defaults.headers.Authorization = `Bearer ${data.token}`;
        setUser(data.user);
        setIsAuth(true);

        toast.success(i18n.t("auth.toasts.success"));
        if (Math.round(dias) < 5) {
          toast.warn(
            `Sua assinatura vence em ${Math.round(dias)} ${
              Math.round(dias) === 1 ? "dia" : "dias"
            } `
          );
        }

        history.push("/tickets");
        setLoading(false);
      } else {
        api.defaults.headers.Authorization = `Bearer ${data.token}`;
        setIsAuth(true);

        toastError(
          `Opss! Sua assinatura venceu ${vencimento}.
Entre em contato com o Suporte para mais informações! `
        );

        history.push("/financeiro-aberto");
        setLoading(false);
      }
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    setLoading(true);

    try {
      await api.delete("/auth/logout");
      setIsAuth(false);
      setUser({});
      localStorage.removeItem("token");
      localStorage.removeItem("cshow");
      api.defaults.headers.Authorization = undefined;
      setLoading(false);
      history.push("/login");
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  const getCurrentUserInfo = async () => {
    try {
      const { data } = await api.get("/auth/me");
      return data;
    } catch (_) {
      return null;
    }
  };

  return {
    isAuth,
    user,
    loading,
    handleLogin,
    handleLogout,
    getCurrentUserInfo,
    socket,
  };
};

export default useAuth;
